// Copyright (c) StrangeBee 2023

import { ReactElement, useState, useEffect, useMemo, useCallback } from 'react';
import { PageInput, PageOutput } from 'thehive-sdk';
import { ICONS } from '@Constants/iconsApp';
import { THVisualizer } from '@Molecules/THVisualizer/THVisualizer';
import { useLocalMessage } from '@Helpers/locale';
import DeleteButton from '@Atoms/Buttons/DeleteButton/DeleteButton';
import THTag from '@Atoms/THTag/THTag';
import { HTMLColors } from '@Constants/colors';
import { THMarkdown } from '@Molecules/Inputs/THMarkdown/THMarkdown';
import { onCaseImageUpload, onOrganisationImageUpload } from '@Helpers/download';
import { useHistory, useParams } from 'react-router-dom';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THAutoComplete } from '@Molecules/Inputs/THAutoComplete/THAutoComplete';
import RouteBuilder from '@Services/RouteBuilder';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { copyToClipboard } from '@Helpers/copy';
import { openMessage } from '@Helpers/notification';

import './MarkdownVisualizer.scss';

interface MarkdownVisualizerProps {
    onEdit: (page: Partial<PageInput>, pageId: string) => any;
    CreateButton: (...args: any[]) => any;
    onDelete: (pageId: string) => any;
    list: PageOutput[];
    modalVisible?: boolean;
    className?: string;
    hasPermissions?: boolean;
    deleteLabel?: string;
    isItemLink?: boolean;
}

export const MarkdownVisualizer = ({
    onEdit,
    CreateButton,
    onDelete,
    list,
    modalVisible,
    className,
    hasPermissions = false,
    deleteLabel,
    isItemLink = true,
}: MarkdownVisualizerProps): ReactElement => {
    const i = useLocalMessage();
    const history = useHistory();

    const { caseId, pageId } = useParams<{ caseId: string; pageId?: string }>();

    const [editMode, setEditMode] = useState(false);
    const [content, setContent] = useState('');
    const [title, setTitle] = useState('');
    const [searchFilter, setSearchFilter] = useState('');
    const [category, setCategory] = useState('');

    const [isLoading, setIsLoading] = useState(false);

    const [selected, setSelected] = useState<PageOutput>();
    const [openKeys, setOpenKeys] = useState<string[]>();

    const dataSource = useMemo(
        () =>
            list?.reduce<Record<string, PageOutput[]>>((acc, currValue) => {
                const search = `${currValue.title.toLowerCase()} ${currValue.content.toLocaleLowerCase()}`;
                if (!search.includes(searchFilter.toLowerCase())) {
                    return acc;
                }

                return {
                    ...acc,
                    [currValue.category || 'default']: [...(acc[currValue.category || 'default'] || []), currValue],
                };
            }, {}),
        [list, searchFilter],
    );

    const getDefaultPage = useCallback(() => {
        if (pageId) {
            const foundPage = list.find(({ _id }) => _id === pageId);
            if (!foundPage) {
                openMessage('error', i('page.view.not-found'));
                return Object.values(dataSource)[0][0];
            }
            return foundPage;
        }
        return Object.values(dataSource)[0][0];
    }, [dataSource, i, list, pageId]);

    useEffect(() => {
        const defaultPage = getDefaultPage();
        if (!selected) {
            setSelected(defaultPage);
            setOpenKeys([defaultPage?.title || '', defaultPage?.category || '']);
            isItemLink && history.push(RouteBuilder.compute('cases.page', { caseId, pageId: defaultPage._id }));
        } else {
            setSelected(list.find(({ _id }) => _id === selected._id));
            isItemLink && history.push(RouteBuilder.compute('cases.page', { caseId, pageId: selected._id }));
        }
    }, [caseId, getDefaultPage, history, isItemLink, list, selected]);

    useEffect(() => {
        if (!modalVisible) {
            setEditMode(false);
        }
    }, [modalVisible]); //eslint-disable-line

    useEffect(() => {
        if (editMode && selected) {
            setContent(selected.content || '');
            setTitle(selected.title || '');
            setCategory(selected.category || '');
        }
    }, [editMode, selected, list]);

    const allCategories = useMemo(() => Object.keys(dataSource), [dataSource]);

    const handleOnEdit = async () => {
        setIsLoading(true);
        try {
            await onEdit({ title, category, content }, selected?._id as string);
            setIsLoading(false);
            // setTimeout is necessary to avoid displaying old page version for few milliseconds
            setTimeout(() => setEditMode(false), 100);
        } catch (e) {
            setIsLoading(false);
        }
    };

    return (
        <THVisualizer<PageOutput>
            className={className}
            title={
                <>
                    <div className="markdown-visualizer-view__visualizer__header__title">
                        {editMode && hasPermissions ? (
                            <div className="header-title__editable">
                                <THInput value={title} onChange={(event): void => setTitle(event.target.value)} />
                                <THAutoComplete
                                    options={allCategories.map((value) => ({ label: value, value }))}
                                    value={category}
                                    onChange={(value): void => setCategory(value)}
                                />
                            </div>
                        ) : (
                            <>
                                <span className="visualizer-header-title__label">{selected?.title}</span>
                                {selected && <THTag value={selected.category} full color={HTMLColors.YELLOW} />}
                            </>
                        )}
                    </div>
                    {hasPermissions && (
                        <div className="markdown-visualizer-view__visualizer__header__actions">
                            {!editMode && !!Object.keys(dataSource).length && (
                                <THButton
                                    onClick={(): void => {
                                        setEditMode(true);
                                    }}
                                    size="small"
                                >
                                    <ICONS.EditIcon />
                                </THButton>
                            )}
                            {editMode && (
                                <THButton
                                    onClick={handleOnEdit}
                                    disabled={!Boolean(title)}
                                    size="small"
                                    type="primary"
                                    loading={isLoading}
                                >
                                    <ICONS.SaveOutlined />
                                </THButton>
                            )}
                            {editMode && (
                                <THButton onClick={(): void => setEditMode(false)} size="small">
                                    <ICONS.CloseIcon />
                                </THButton>
                            )}
                        </div>
                    )}
                </>
            }
            filter={
                <>
                    {!editMode && hasPermissions && (
                        <CreateButton
                            allCategories={allCategories}
                            className="markdown-visualizer-view__visualizer__header__filter__add-button"
                        />
                    )}
                    <div className="markdown-visualizer-view__visualizer__header__filter">
                        <THInput
                            className="filter-input"
                            value={searchFilter}
                            placeholder={i('page.view.search')}
                            onChange={(event): void => setSearchFilter(event.target.value)}
                        />
                    </div>
                </>
            }
            dataSource={editMode && selected ? [selected] : dataSource}
            content={
                editMode && hasPermissions ? (
                    <THMarkdown
                        key="edit"
                        value={content}
                        onChange={(value) => setContent(value)}
                        onImageUpload={caseId ? onCaseImageUpload(caseId) : onOrganisationImageUpload}
                    />
                ) : (
                    <THMarkdown key="view" value={selected?.content} disabled borderless />
                )
            }
            selectedState={{ selected, setSelected }}
            openKeysState={{ openKeys, setOpenKeys }}
            itemKey="_id"
            itemClassName="markdown-visualizer-item"
        >
            {(item): ReactElement => {
                const pageLink = RouteBuilder.compute('cases.page', { caseId, pageId: item._id });

                return (
                    <>
                        {item.title}
                        {hasPermissions && (
                            <span className="markdown-visualizer-item__actions">
                                {isItemLink && (
                                    <THTooltip title={i('page.view.copy')} placement="bottom">
                                        <THButton
                                            type="link"
                                            icon={<ICONS.Link />}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                copyToClipboard(
                                                    window.location.origin + pageLink,
                                                    i('notification.copy'),
                                                );
                                            }}
                                            size="small"
                                        />
                                    </THTooltip>
                                )}
                                <DeleteButton
                                    label={deleteLabel || i('page.view.delete')}
                                    onClick={(event): void => event.stopPropagation()}
                                    onConfirm={(e): void => {
                                        onDelete(item._id);
                                        e?.stopPropagation();
                                        let firstPage = Object.values(dataSource)[0][0];
                                        if (item._id === firstPage._id) {
                                            firstPage =
                                                Object.values(dataSource)[0]?.[1] || Object.values(dataSource)[1]?.[0];
                                        }
                                        setSelected(firstPage);
                                        setOpenKeys([...(openKeys || []), firstPage?.category, firstPage?.title]);
                                    }}
                                />
                            </span>
                        )}
                    </>
                );
            }}
        </THVisualizer>
    );
};
